import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'

interface MetadataKey {
  key: string
  values: string[]
  total: number
  has_more: boolean
}

interface MetadataKeysResponse {
  keys: MetadataKey[]
}

interface MetadataKeyValuesResponse {
  key: string
  values: string[]
  total: number
  skip: number
  limit: number
}

// Cache configuration - only for metadata
const METADATA_CACHE_KEY = 'fenra_metadata_cache'
const CACHE_TTL_MS = 60 * 1000 // 1 minute

interface CachedMetadata {
  data: MetadataKeysResponse
  timestamp: number
}

/**
 * Get cached metadata from localStorage if fresh (< 1 minute old)
 */
function getCachedMetadata(): MetadataKeysResponse | null {
  try {
    const cached = localStorage.getItem(METADATA_CACHE_KEY)
    if (!cached) return null

    const parsed: CachedMetadata = JSON.parse(cached)
    const age = Date.now() - parsed.timestamp

    if (age > CACHE_TTL_MS) {
      // Cache expired, remove it
      localStorage.removeItem(METADATA_CACHE_KEY)
      return null
    }

    return parsed.data
  } catch {
    return null
  }
}

/**
 * Store metadata in localStorage with timestamp
 */
function setCachedMetadata(data: MetadataKeysResponse): void {
  try {
    const cached: CachedMetadata = {
      data,
      timestamp: Date.now(),
    }
    localStorage.setItem(METADATA_CACHE_KEY, JSON.stringify(cached))
  } catch {
    // Ignore localStorage errors
  }
}

async function getKeys(): Promise<MetadataKeysResponse> {
  // Check localStorage cache first
  const cached = getCachedMetadata()
  if (cached) {
    return cached
  }

  // Fetch from API
  const response = await fetchWithAuth('/api/v1/metadata-registry/keys')

  // Store in localStorage
  if (response?.keys) {
    setCachedMetadata(response)
  }

  return response
}

async function getKeyValues(
  key: string,
  search: string = '',
  skip: number = 0,
  limit: number = 50
): Promise<MetadataKeyValuesResponse> {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
  })

  if (search) {
    params.append('search', search)
  }

  return fetchWithAuth(`/api/v1/metadata-registry/keys/${encodeURIComponent(key)}/values?${params}`)
}

export function useMetadataKeys() {
  return useQuery({
    queryKey: ['metadata', 'keys'],
    queryFn: getKeys,
    staleTime: CACHE_TTL_MS, // Don't refetch within 1 minute
    gcTime: CACHE_TTL_MS, // Keep in React Query cache for 1 minute
  })
}

export function useMetadataKeyValues(
  key: string | null,
  search: string = '',
  skip: number = 0,
  limit: number = 50,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['metadata', 'keys', key, 'values', search, skip, limit],
    queryFn: () => getKeyValues(key!, search, skip, limit),
    enabled: enabled && key !== null,
    staleTime: CACHE_TTL_MS, // Don't refetch within 1 minute
    gcTime: CACHE_TTL_MS,
  })
}

/**
 * Get cached metadata keys from React Query cache (synchronous)
 */
export function useCachedMetadataKeys(): string[] {
  const queryClient = useQueryClient()
  const cachedData = queryClient.getQueryData<MetadataKeysResponse>(['metadata', 'keys'])
  return cachedData?.keys?.map((k) => k.key) ?? []
}

/**
 * Get initial values for a specific key from cached metadata
 */
export function useCachedInitialValues(key: string | null): { values: string[]; total: number } | null {
  const queryClient = useQueryClient()
  if (!key) return null

  const cachedData = queryClient.getQueryData<MetadataKeysResponse>(['metadata', 'keys'])
  const keyData = cachedData?.keys?.find((k) => k.key === key)

  if (!keyData) return null
  return { values: keyData.values, total: keyData.total }
}
